import React, { useEffect, useState, useRef } from 'react';
import { Navigate, useParams, useNavigate } from 'react-router-dom';
import { Banner, Button, TextField, Spinner, Icon, Select, IndexTable, Text, useIndexResourceState, Badge } from '@shopify/polaris';
import { SearchIcon, ChevronLeftIcon, ChevronRightIcon } from '@shopify/polaris-icons';
import { useAuth, useAuthFetch } from './AuthComponent';
import { useStore } from './StoreContext';

import { hasRole } from './constants/roles';
const COLLECTIONS_ROLES = ['admin', 'user', 'crm', 'site_manager', 'franchise'];
const EDIT_ROLES        = ['admin', 'user', 'crm', 'site_manager'];
// TP price edits are admin-only — all other edit roles can add/remove products
// from the collection but see the price as read-only (falls back to Shopify
// price when no TP override is set).
const PRICE_EDIT_ROLES  = ['admin'];

const SORT_OPTIONS = [
    { label: 'Manually',         value: 'MANUAL' },
    { label: 'Best selling',     value: 'BEST_SELLING' },
    { label: 'Product title A-Z', value: 'ALPHA_ASC' },
    { label: 'Product title Z-A', value: 'ALPHA_DESC' },
    { label: 'Highest price',    value: 'PRICE_DESC' },
    { label: 'Lowest price',     value: 'PRICE_ASC' },
    { label: 'Newest',           value: 'CREATED_DESC' },
    { label: 'Oldest',           value: 'CREATED' },
];

function useDebounced(value, ms) {
    const [v, setV] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setV(value), ms);
        return () => clearTimeout(t);
    }, [value, ms]);
    return v;
}

function formatRelative(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    const days = Math.floor((Date.now() - d.getTime()) / 86400000);
    if (days < 1) return 'today';
    if (days < 2) return 'yesterday';
    if (days < 30) return `${days}d ago`;
    return d.toLocaleDateString();
}

export default function Collections() {
    const { user } = useAuth();
    const { activeStore, STORE_DISPLAY_NAMES } = useStore();
    const storeFetch = useAuthFetch();

    // Per-user page zoom (persisted to localStorage). 0.85 baseline matches the
    // shrink applied via styles.css. +/- buttons next to "Collections" step by
    // 5% with sane bounds.
    const [zoomLevel, setZoomLevel] = useState(() => {
        const v = parseFloat(localStorage.getItem('collections.zoom'));
        return Number.isFinite(v) && v >= 0.5 && v <= 1.5 ? v : 0.85;
    });
    useEffect(() => {
        localStorage.setItem('collections.zoom', String(zoomLevel));
    }, [zoomLevel]);
    const adjustZoom = (delta) => setZoomLevel(z => {
        const n = Math.round((z + delta) * 100) / 100;
        return Math.max(0.5, Math.min(1.5, n));
    });

    const pageRef = useRef(null);
    // Compensate zoom's tendency to shrink both visual size AND layout box:
    // measure the parent's actual pixel height, divide by zoomLevel, set as
    // the page's inline height. Re-measures on viewport resize + zoom change.
    const [pageHeightPx, setPageHeightPx] = useState(0);
    useEffect(() => {
        const el = pageRef.current;
        const parent = el?.parentElement;
        if (!parent) return;
        const apply = () => setPageHeightPx(Math.round(parent.clientHeight / zoomLevel));
        apply();
        const ro = new ResizeObserver(apply);
        ro.observe(parent);
        window.addEventListener('resize', apply);
        return () => { ro.disconnect(); window.removeEventListener('resize', apply); };
    }, [zoomLevel]);
    // Route-style deep-link: /store/:storeSlug/products/collection/:collectionId.
    // collectionId is the numeric Shopify Collection ID — backend prefixes
    // `gid://shopify/Collection/` when calling the Admin API.
    const { storeSlug, collectionId: routeCollectionId } = useParams();
    const navigate = useNavigate();
    const initialCollectionIdRef = useRef(routeCollectionId || null);
    const deepLinkAppliedRef = useRef(false);

    // Build the numeric ID from a GID for use in the URL (the inverse of
    // what the backend does on read). Returns null if `gid` doesn't look
    // like a Shopify Collection GID.
    const numericIdFromGid = (gid) => {
        if (!gid) return null;
        const m = String(gid).match(/\/Collection\/(\d+)$/);
        return m ? m[1] : (String(gid).match(/^\d+$/) ? String(gid) : null);
    };

    const canView = hasRole(user, COLLECTIONS_ROLES);
    const canEdit = hasRole(user, EDIT_ROLES);
    const canEditPrice = hasRole(user, PRICE_EDIT_ROLES);
    const readOnly = !canEdit;

    // Drawer state
    const [collections, setCollections]     = useState([]);
    const [drawerQuery, setDrawerQuery]     = useState('');
    const [drawerLoading, setDrawerLoading] = useState(true);
    const [drawerError, setDrawerError]     = useState(null);
    // IndexTable sort state — 4 columns: Name (0), Creator (1), Products (2), Last Updated (3)
    const [sortColIdx, setSortColIdx]       = useState(0);          // default: sort by Name
    const [sortDir, setSortDir]             = useState('ascending'); // 'ascending' | 'descending'

    // Builder state
    const [activeId, setActiveId]       = useState(null);
    const [name, setName]               = useState('');
    const [items, setItems]             = useState([]);  // [{id, title, vendor, image, price, ...}]
    const [savedUrl, setSavedUrl]       = useState(null);
    const [sortOrder, setSortOrder]     = useState('MANUAL');
    const [baseline, setBaseline]       = useState({ name: '', ids: [], sortOrder: 'MANUAL' });
    // Per-product team_portal_collection_price edits. Both dicts keyed by GID;
    // value is the amount string (empty string = unset/delete). Baseline tracks
    // value at last load/save so we only POST changed rows.
    const [tpPrices, setTpPrices]               = useState({});
    const [tpPricesBaseline, setTpPricesBaseline] = useState({});
    const [saving, setSaving]           = useState(false);
    const [deleting, setDeleting]       = useState(false);
    const [saveError, setSaveError]     = useState(null);
    const [droppedNotice, setDroppedNotice] = useState(null);
    const [reorderPending, setReorderPending] = useState(false);
    const [copyState, setCopyState]     = useState(null);  // 'ok' | 'fail' | null
    const [pendingNav, setPendingNav]   = useState(null);  // {type: 'open'|'new', payload?}
    const [loadingActive, setLoadingActive] = useState(false);
    const [dragIdx, setDragIdx] = useState(null);
    const [dropIdx, setDropIdx] = useState(null);

    // Search state
    const [q, setQ]                           = useState('');
    const debounced                           = useDebounced(q, 300);
    const [results, setResults]               = useState([]);
    const [searching, setSearching]           = useState(false);
    const [titleCursor, setTitleCursor]       = useState(null);
    const [variantsCursor, setVariantsCursor] = useState(null);
    const [hasMore, setHasMore]               = useState(false);
    const [lastFetchedQuery, setLastFetchedQuery] = useState('');
    // Client-side sort for the search results (sorts only over what's been
    // loaded; doesn't change the server-side fetch order). 'relevance' = as
    // returned by the API; 'inventory_desc' = most-in-stock first;
    // 'price_asc' / 'price_desc' = price-numeric. (#3)
    const [resultsSort, setResultsSort]       = useState('relevance');

    // Clone is now client-side (drafts the source's products into the
    // builder; doesn't touch Shopify until Save). No in-flight state needed.

    // Only admins can dirty prices. For everyone else, the input is disabled
    // and the diff would always be empty — short-circuit so we never accidentally
    // mark the collection dirty from a non-admin's read-only price view.
    const pricesDirty = canEditPrice && items.some(p => {
        const cur  = (tpPrices[p.id] ?? '').trim();
        const base = (tpPricesBaseline[p.id] ?? '').trim();
        return cur !== base;
    });
    const dirty = (
        name.trim() !== (baseline.name || '').trim() ||
        items.map(i => i.id).join('|') !== baseline.ids.join('|') ||
        sortOrder !== (baseline.sortOrder || 'MANUAL') ||
        pricesDirty
    );
    // Empty product lists are now allowed (BE matches as of 2026-06-18). The
    // user can remove the last item and Save — useful mid-cleanup or for a
    // named-but-empty placeholder shell. To get rid of the collection
    // entirely, use the Delete button (admin only).
    const canSave = !readOnly && name.trim().length > 0 && (dirty || !activeId);

    const buildPricesFromProducts = (products) => {
        const dict = {};
        (products || []).forEach(p => {
            dict[p.id] = p.tp_price?.amount ? String(p.tp_price.amount) : '';
        });
        return dict;
    };

    const actuallyOpen = (id) => {
        setLoadingActive(true);
        setSaveError(null);
        setDroppedNotice(null);
        setReorderPending(false);
        // Reflect the active collection in the URL so the view is linkable.
        // Use replace (not push) so back-button still leaves the page rather
        // than walking through every collection the user opened.
        const numeric = numericIdFromGid(id);
        if (numeric && storeSlug) {
            navigate(`/store/${storeSlug}/products/collection/${numeric}`,
                     { replace: true });
        }
        storeFetch(`/api/collections/${encodeURIComponent(id)}`)
            .then(r => {
                // 404 = collection deleted in Shopify (e.g. someone else
                // deleted it, or this deep-link points at a stale GID). Don't
                // surface a raw HTTP error — drop the bad target, drop the
                // drawer row, bounce to the base /collections URL so the next
                // click works normally, and refresh the drawer so the deleted
                // row vanishes.
                if (r.status === 404) {
                    setCollections(cs => cs.filter(c => c.id !== id));
                    setActiveId(null);
                    setName('');
                    setItems([]);
                    setSavedUrl('');
                    if (storeSlug) {
                        navigate(`/store/${storeSlug}/products/collections`,
                                 { replace: true });
                    }
                    loadDrawer();
                    return null;
                }
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                return r.json();
            })
            .then(data => {
                if (!data) return; // 404 path bailed above
                setActiveId(data.id);
                const stripped = (data.title || '').replace(/^TP -\s*/, "");
                const so = data.sort_order || 'MANUAL';
                setName(stripped);
                setItems(data.products || []);
                setSavedUrl(data.admin_url || null);
                setSortOrder(so);
                setBaseline({ name: stripped, ids: (data.products || []).map(p => p.id), sortOrder: so });
                const pd = buildPricesFromProducts(data.products);
                setTpPrices(pd);
                setTpPricesBaseline(pd);
            })
            .catch(err => setSaveError(err.message))
            .finally(() => setLoadingActive(false));
    };

    // Deep-link: open the collection referenced in ?id=<gid> on first mount,
    // once the drawer has finished loading (we don't need the drawer's data,
    // but waiting avoids the open call racing with the initial drawer fetch
    // and the loading spinner flicker).
    useEffect(() => {
        if (deepLinkAppliedRef.current) return;
        if (drawerLoading) return;
        const target = initialCollectionIdRef.current;
        if (!target) return;
        deepLinkAppliedRef.current = true;
        actuallyOpen(target);
        // actuallyOpen / setSearchParams are stable enough for this one-shot
        // intent — re-running on every render would re-open mid-edit.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [drawerLoading]);
    const openCollection = (id) => {
        if (dirty) { setPendingNav({ type: 'open', payload: id }); return; }
        actuallyOpen(id);
    };

    const actuallyStartNew = () => {
        setActiveId(null); setName(''); setItems([]); setSavedUrl(null);
        setSortOrder('MANUAL');
        setBaseline({ name: '', ids: [], sortOrder: 'MANUAL' });
        setTpPrices({}); setTpPricesBaseline({});
        setSaveError(null); setDroppedNotice(null); setReorderPending(false);
        // Drop the /collection/:id segment when starting a fresh draft.
        if (storeSlug) {
            navigate(`/store/${storeSlug}/products/collections`, { replace: true });
        }
    };
    const startNew = () => {
        if (dirty) { setPendingNav({ type: 'new' }); return; }
        actuallyStartNew();
    };

    const removeItem = (id) => {
        setItems(items.filter(p => p.id !== id));
        setTpPrices(prev => { const n = { ...prev }; delete n[id]; return n; });
        setTpPricesBaseline(prev => { const n = { ...prev }; delete n[id]; return n; });
    };

    const setTpPrice = (gid, raw) => {
        // Accept anything; backend re-validates. Reject obvious non-numerics
        // for snappier UI feedback, but allow '' and partial decimals.
        const s = String(raw ?? '').replace(/[^\d.]/g, '');
        // Collapse multiple dots: keep first only.
        const parts = s.split('.');
        const clean = parts.length > 1 ? `${parts[0]}.${parts.slice(1).join('')}` : s;
        setTpPrices(prev => ({ ...prev, [gid]: clean }));
    };

    const moveItem = (fromIdx, toIdx) => {
        if (fromIdx === toIdx || toIdx < 0 || toIdx >= items.length) return;
        const next = [...items];
        const [moved] = next.splice(fromIdx, 1);
        next.splice(toIdx, 0, moved);
        setItems(next);
    };
    const onDragStart = (idx) => (e) => {
        setDragIdx(idx);
        e.dataTransfer.effectAllowed = 'move';
        // Drag = user wants manual ordering. Auto-switch if currently sorted differently.
        if (sortOrder !== 'MANUAL') setSortOrder('MANUAL');
    };
    const onDragOver = (idx) => (e) => {
        e.preventDefault();
        if (dragIdx === null || dragIdx === idx) return;
        setDropIdx(idx);
    };
    const onDrop = (idx) => (e) => {
        e.preventDefault();
        if (dragIdx === null) return;
        moveItem(dragIdx, idx);
        setDragIdx(null); setDropIdx(null);
    };
    const onDragEnd = () => { setDragIdx(null); setDropIdx(null); };

    const save = () => {
        if (!canSave || saving) return;
        setSaving(true); setSaveError(null);
        // Diff prices: only send rows where the input differs from baseline.
        // Empty string -> backend interprets as delete.
        // Non-admins can't change prices — never include tp_prices in their
        // save payload (backend also rejects, this is just belt-and-braces).
        const priceChanges = canEditPrice
            ? items
                .map(p => {
                    const cur  = (tpPrices[p.id] ?? '').trim();
                    const base = (tpPricesBaseline[p.id] ?? '').trim();
                    if (cur === base) return null;
                    return { product_id: p.id, amount: cur === '' ? null : cur };
                })
                .filter(Boolean)
            : [];
        const body = {
            name: name.trim(),
            product_ids: items.map(p => p.id),
            sort_order: sortOrder,
            tp_prices: priceChanges.length > 0 ? priceChanges : undefined,
        };
        const url = activeId ? `/api/collections/${encodeURIComponent(activeId)}` : '/api/collections';
        const method = activeId ? 'PUT' : 'POST';
        storeFetch(url, {
            method,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        })
            .then(async r => {
                const data = await r.json().catch(() => ({}));
                if (!r.ok) {
                    const msg = data.detail || `HTTP ${r.status}`;
                    throw new Error(msg);
                }
                return data;
            })
            .then(data => {
                setActiveId(data.id);
                const stripped = (data.title || '').replace(/^TP -\s*/, "");
                const so = data.sort_order || 'MANUAL';
                setName(stripped);
                setItems(data.products || []);
                setSavedUrl(data.admin_url);
                setSortOrder(so);
                setBaseline({ name: stripped, ids: (data.products || []).map(p => p.id), sortOrder: so });
                const pd = buildPricesFromProducts(data.products);
                setTpPrices(pd);
                setTpPricesBaseline(pd);
                if ((data.dropped || []).length > 0) {
                    setDroppedNotice(
                        `${data.dropped.length} product(s) were no longer available and were skipped.`
                    );
                }
                if ((data.price_failures || []).length > 0) {
                    setSaveError(
                        `Some price changes failed: ` +
                        data.price_failures.map(f => `${f.product_id.split('/').pop()} (${f.error || 'error'})`).join(', ')
                    );
                }
                if (data.reorder_pending) setReorderPending(true);
                // Optimistic drawer refresh: synthesize a list entry from the
                // save response and merge it into state immediately. Without
                // this, a brand-new collection sits behind Shopify's search
                // index lag (the same propagation delay that affects
                // /api/orders/search) and doesn't appear in the left column
                // for 5-15 seconds even though save succeeded.
                if (!activeId) {
                    const meName = (`${user?.first_name || ''} ${user?.last_name || ''}`).trim()
                                   || user?.sub || null;
                    const nowIso = new Date().toISOString();
                    setCollections(prev => {
                        // Dedupe in case loadDrawer fires before us with the
                        // new row.
                        if (prev.some(c => c.id === data.id)) return prev;
                        return [...prev, {
                            id: data.id,
                            title: data.title,
                            products_count: (data.products || []).length,
                            created_by_display: meName,
                            last_user_edit_at: nowIso,
                            created_at: nowIso,
                            updated_at: nowIso,
                        }];
                    });
                }
                loadDrawer();
            })
            .catch(err => setSaveError(err.message))
            .finally(() => setSaving(false));
    };

    // Clone the currently-open collection (#5).
    //
    // CLIENT-SIDE ONLY: this DOES NOT persist anything to Shopify. It seeds
    // the builder with the source collection's products + TP prices + sort
    // order, generates a "(Clone)" / "(Clone N)" name that's unique against
    // the loaded drawer, and clears `activeId` so the next Save runs the
    // create-collection path. If the user navigates away or hits "Discard
    // changes" before Save, nothing is created.
    const cloneCollection = () => {
        if (!activeId) return;
        // Build a unique "(Clone)" / "(Clone N)" name. Strip the existing
        // TP prefix + any prior "(Clone)" suffix so a clone of a clone reads
        // "Foo (Clone 2)" not "Foo (Clone) (Clone)".
        const cleanBase = (name || '')
            .replace(/^TP\s*-\s*/i, '')
            .replace(/\s*\(Clone(?:\s+\d+)?\)\s*$/i, '')
            .trim();
        const existingTitles = new Set(
            (collections || []).map(c => (c.title || '').replace(/^TP\s*-\s*/, '').trim().toLowerCase())
        );
        const isTaken = (candidate) => existingTitles.has(candidate.trim().toLowerCase());
        let cloneName = `${cleanBase} (Clone)`;
        if (isTaken(cloneName)) {
            for (let i = 2; i < 100; i++) {
                const c = `${cleanBase} (Clone ${i})`;
                if (!isTaken(c)) { cloneName = c; break; }
            }
        }
        // Switch the builder into draft-create mode — clears activeId so
        // Save runs POST /api/collections instead of PUT, and resets the
        // baseline so `dirty` reflects this clone's pending state.
        setActiveId(null);
        setSavedUrl(null);
        setName(cloneName);
        // items + sortOrder + tpPrices remain from the source; baseline is
        // empty so everything reads as dirty/pending until Save.
        setBaseline({ name: '', ids: [], sortOrder: 'MANUAL' });
        setTpPricesBaseline({});
        setSaveError(null);
        setDroppedNotice(null);
        setReorderPending(false);
        // Reflect the new draft in the URL — clear any deep-link to the source.
        if (storeSlug) {
            navigate(`/store/${storeSlug}/products/collections`, { replace: true });
        }
    };

    // Hard-delete the currently-open collection from Shopify. Admin only —
    // the backend re-enforces the role gate; this just hides the button for
    // non-admins. Confirms first; on success drops the row from the drawer,
    // resets the builder, clears the deep-link from the URL.
    const deleteCollection = async () => {
        if (!activeId) return;
        const label = name?.trim() ? `"TP - ${name.trim()}"` : 'this collection';
        if (!window.confirm(
            `Permanently delete ${label} from Shopify?\n\n` +
            `This cannot be undone. Any storefront pages or app references will break.`
        )) return;
        setDeleting(true);
        try {
            const numeric = numericIdFromGid(activeId) || activeId;
            const res = await storeFetch(`/api/collections/${numeric}`,
                                         { method: 'DELETE' });
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j.detail || `HTTP ${res.status}`);
            }
            // Drop from the drawer list and clear the builder.
            setCollections(cs => cs.filter(c => c.id !== activeId));
            setActiveId(null);
            setName('');
            setItems([]);
            setSavedUrl('');
            // `dirty` is a derived value (line ~161), not a setState. Clearing
            // name/items/savedUrl above already drives it back to false.
            setReorderPending(false);
            if (storeSlug) {
                navigate(`/store/${storeSlug}/products/collections`, { replace: true });
            }
        } catch (e) {
            alert(`Delete failed: ${e.message || e}`);
        } finally {
            setDeleting(false);
        }
    };

    const copyUrl = () => {
        if (!savedUrl) return;
        const flash = (state) => {
            setCopyState(state);
            setTimeout(() => setCopyState(null), 1500);
        };
        if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(savedUrl)
                .then(() => flash('ok'))
                .catch(() => flash('fail'));
        } else {
            flash('fail');
        }
    };

    // Fetch on debounced query change (resets cursors)
    useEffect(() => {
        const abort = new AbortController();
        if (debounced.trim().length < 2) {
            setResults([]); setHasMore(false);
            setTitleCursor(null); setVariantsCursor(null);
            setLastFetchedQuery(debounced);
            return;
        }
        setSearching(true);
        const params = new URLSearchParams({ q: debounced.trim() });
        storeFetch(`/api/products/search-catalog?${params}`, { signal: abort.signal })
            .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
            .then(data => {
                setResults(data.results || []);
                setTitleCursor(data.next_title_cursor || null);
                setVariantsCursor(data.next_variants_cursor || null);
                setHasMore(!!data.has_more);
                setLastFetchedQuery(debounced);
            })
            .catch(err => { if (err.name !== 'AbortError') setResults([]); })
            .finally(() => setSearching(false));
        return () => abort.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debounced, activeStore]);

    const loadMore = () => {
        if (!hasMore || searching) return;
        setSearching(true);
        const params = new URLSearchParams({ q: debounced.trim() });
        if (titleCursor)    params.set('title_cursor',    titleCursor);
        if (variantsCursor) params.set('variants_cursor', variantsCursor);
        storeFetch(`/api/products/search-catalog?${params}`)
            .then(r => r.json())
            .then(data => {
                const seen = new Set(results.map(r => r.id));
                const fresh = (data.results || []).filter(r => !seen.has(r.id));
                setResults(prev => [...prev, ...fresh]);
                setTitleCursor(data.next_title_cursor || null);
                setVariantsCursor(data.next_variants_cursor || null);
                // dedup-loop guard: stop if Shopify keeps returning rows we already had
                setHasMore(!!data.has_more && fresh.length > 0);
            })
            .finally(() => setSearching(false));
    };

    const inCollection = new Set(items.map(i => i.id));
    const addToCollection = (product) => {
        if (inCollection.has(product.id)) return;
        setItems(prev => [...prev, product]);
        // Seed price input + baseline from the search result so an unchanged
        // value doesn't mark the collection dirty for a price write.
        const amt = product.tp_price?.amount ? String(product.tp_price.amount) : '';
        setTpPrices(prev => ({ ...prev, [product.id]: amt }));
        setTpPricesBaseline(prev => ({ ...prev, [product.id]: amt }));
    };

    // beforeunload guard on dirty
    useEffect(() => {
        if (!dirty) return;
        const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [dirty]);

    const pageHeaderRef = useRef(null);
    const [headerHeight, setHeaderHeight] = useState(0);
    useEffect(() => {
        if (!pageHeaderRef.current) return;
        const ro = new ResizeObserver(() => {
            setHeaderHeight(pageHeaderRef.current?.offsetHeight || 0);
        });
        ro.observe(pageHeaderRef.current);
        return () => ro.disconnect();
    }, []);

    const loadDrawer = () => {
        setDrawerLoading(true);
        setDrawerError(null);
        storeFetch('/api/collections')
            .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
            .then(data => {
                const fresh = data.collections || [];
                const freshIds = new Set(fresh.map(c => c.id));
                // Preserve any optimistic entries that the freshly-fetched
                // Shopify list doesn't yet contain — newly-created collections
                // can take 5–15 seconds to appear in Shopify's collections
                // query (same search-index lag we fixed for orders). Without
                // this merge, the optimistic add we did in save() gets wiped
                // on the very next loadDrawer fetch and the new collection
                // vanishes until the user refreshes the page.
                setCollections(prev => {
                    const pending = (prev || []).filter(c => !freshIds.has(c.id));
                    return [...fresh, ...pending];
                });
            })
            .catch(err => setDrawerError(err.message))
            .finally(() => setDrawerLoading(false));
    };
    useEffect(() => {
        // Clear the list immediately on store switch so the old store's
        // collections aren't visible during the new store's fetch. The
        // optimistic-pending merge inside loadDrawer was preserving prior
        // entries by ID; on a cross-store switch every prior ID is "missing"
        // from the new store's fresh list and got incorrectly kept.
        setCollections([]);
        if (canView && activeStore && activeStore !== 'all') loadDrawer();
        else setDrawerLoading(false);
        /* eslint-disable-next-line */
    }, [activeStore]);

    if (!canView) return <Navigate to={`/store/${activeStore}/orders`} replace />;

    // Collections operate on a single Shopify store at a time — every read
    // and write needs a specific store_key. Surface a friendly prompt instead
    // of hitting the API and getting a 502.
    if (activeStore === 'all') {
        return (
            <div className="collections-page">
                <div className="sticky-page-header">
                    <h1 className="page-title">Collections</h1>
                    <div className="page-subtitle">
                        Build &amp; manage Team Portal product collections · all prefixed <code>TP -</code>
                    </div>
                </div>
                <div style={{ marginTop: 24, maxWidth: 720 }}>
                    <Banner tone="info" title="Switch to a specific store to use Collections">
                        <p>
                            Collections are scoped to one Shopify store. Pick a store from
                            the <strong>store switcher</strong> in the top-left sidebar
                            (currently set to <em>All Stores</em>) — then this page will
                            list every <code>TP -</code> collection for that store.
                        </p>
                    </Banner>
                </div>
            </div>
        );
    }

    // Filter by free-text search, then sort by the user-selected column.
    // "Last Updated" prefers last_user_edit_at (user-stamped on save) over
    // created_at (Shopify creation time, lazy-cached from REST). Shopify's
    // updatedAt is NEVER used here — it bumps on every metafield write.
    const effectiveUpdatedIso = (c) =>
        c.last_user_edit_at || c.created_at || null;
    const lastUpdatedTs = (c) => {
        const iso = effectiveUpdatedIso(c);
        return iso ? new Date(iso).getTime() : 0;
    };
    const sortKey = ['name', 'creator', 'products', 'updated'][sortColIdx] || 'name';
    const sortMul = sortDir === 'descending' ? -1 : 1;
    const filteredCollections = collections
        .filter(c => c.title.toLowerCase().includes(drawerQuery.trim().toLowerCase()))
        .slice()
        .sort((a, b) => {
            if (sortKey === 'name') {
                const aT = (a.title || '').replace(/^TP -\s*/, '').toLowerCase();
                const bT = (b.title || '').replace(/^TP -\s*/, '').toLowerCase();
                return aT.localeCompare(bT) * sortMul;
            }
            if (sortKey === 'creator') {
                return ((a.created_by_display || '').toLowerCase())
                    .localeCompare((b.created_by_display || '').toLowerCase()) * sortMul;
            }
            if (sortKey === 'products') {
                return ((a.products_count || 0) - (b.products_count || 0)) * sortMul;
            }
            // updated
            return (lastUpdatedTs(a) - lastUpdatedTs(b)) * sortMul;
        });

    // Client-side sort of the loaded search results (#3). Applies on top of
    // whatever order the API returned (relevance). 'relevance' keeps API
    // order, the others reorder by numeric inventory or price.
    const numPrice = (p) => {
        const a = p.price;
        const n = a == null ? NaN : parseFloat(a);
        return isNaN(n) ? null : n;
    };
    const sortedResults = (() => {
        if (resultsSort === 'relevance') return results;
        const arr = results.slice();
        if (resultsSort === 'inventory_desc') {
            arr.sort((a, b) => (b.total_inventory || 0) - (a.total_inventory || 0));
        } else if (resultsSort === 'inventory_asc') {
            arr.sort((a, b) => (a.total_inventory || 0) - (b.total_inventory || 0));
        } else if (resultsSort === 'price_asc') {
            arr.sort((a, b) => {
                const pa = numPrice(a), pb = numPrice(b);
                if (pa == null && pb == null) return 0;
                if (pa == null) return 1;  // unknowns sink
                if (pb == null) return -1;
                return pa - pb;
            });
        } else if (resultsSort === 'price_desc') {
            arr.sort((a, b) => {
                const pa = numPrice(a), pb = numPrice(b);
                if (pa == null && pb == null) return 0;
                if (pa == null) return 1;
                if (pb == null) return -1;
                return pb - pa;
            });
        }
        return arr;
    })();

    return (
        <div className="collections-page"
             ref={pageRef}
             style={{
                 zoom: zoomLevel,
                 // Compensate for the fact that zoom shrinks both visual size
                 // AND layout box: use the viewport's actual height in pixels
                 // divided by zoomLevel so the rendered height fills the
                 // .page-content scroll area exactly. minHeight wins over the
                 // flex container's auto-sizing-to-content behavior.
                 ...(pageHeightPx ? { minHeight: `${pageHeightPx}px`, height: `${pageHeightPx}px` } : {}),
             }}>
            <div className="sticky-page-header" ref={pageHeaderRef}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <h1 className="page-title" style={{ margin: 0 }}>Collections</h1>
                    <button type="button" aria-label="Decrease font size"
                            className="collections-zoom-btn"
                            onClick={() => adjustZoom(-0.05)}
                            disabled={zoomLevel <= 0.5}>−</button>
                    <button type="button" aria-label="Increase font size"
                            className="collections-zoom-btn"
                            onClick={() => adjustZoom(+0.05)}
                            disabled={zoomLevel >= 1.5}>+</button>
                    <span style={{ fontSize: 12, color: '#6d7175' }}>
                        {Math.round(zoomLevel * 100)}%
                    </span>
                </div>
                <div className="page-subtitle">
                    Build &amp; manage Team Portal product collections · all prefixed <code>TP -</code>
                    {' · Store: '}{STORE_DISPLAY_NAMES?.[activeStore] || activeStore}
                </div>
            </div>

            {readOnly && (
                <Banner tone="info">
                    View only. Franchise users can browse collections but can&rsquo;t create or edit them.
                </Banner>
            )}

            {pendingNav && (
                <Banner tone="warning"
                        action={{ content: 'Discard changes', onAction: () => {
                            const p = pendingNav; setPendingNav(null);
                            if (p.type === 'open') actuallyOpen(p.payload);
                            else if (p.type === 'new') actuallyStartNew();
                        }}}
                        secondaryAction={{ content: 'Cancel', onAction: () => setPendingNav(null) }}>
                    Discard unsaved changes to "{name || '(new collection)'}"?
                </Banner>
            )}

            <div className="collections-page__grid" style={{ marginTop: 16 }}>
                {/* Column 1: Drawer */}
                <aside className="collections-drawer">
                    <div className="collections-drawer__sticky-top">
                        <div className="collections-drawer__header">
                            <span className="collections-drawer__title">TP COLLECTIONS</span>
                            <span className="collections-drawer__count">{collections.length}</span>
                        </div>
                        <TextField label="Filter" labelHidden value={drawerQuery}
                                   onChange={setDrawerQuery}
                                   placeholder="Find a collection…"
                                   prefix={<Icon source={SearchIcon} />} autoComplete="off" />
                        {!readOnly && (
                            <Button fullWidth onClick={startNew}>
                                + New collection
                            </Button>
                        )}
                    </div>
                    {drawerLoading && <div className="collections-drawer__loading"><Spinner size="small" /></div>}
                    {drawerError && (
                        <Banner tone="critical" action={{ content: 'Retry', onAction: loadDrawer }}>
                            {drawerError}
                        </Banner>
                    )}
                    {!drawerLoading && !drawerError && (
                        <div className="collections-drawer__table">
                            <IndexTable
                                resourceName={{singular: 'collection', plural: 'collections'}}
                                itemCount={filteredCollections.length}
                                selectable={false}
                                headings={[
                                    {title: 'Name'},
                                    {title: 'Creator'},
                                    {title: 'Products', alignment: 'end'},
                                    {title: 'Last Updated', alignment: 'end'},
                                ]}
                                sortable={[true, true, true, true]}
                                sortDirection={sortDir}
                                sortColumnIndex={sortColIdx}
                                onSort={(idx, dir) => { setSortColIdx(idx); setSortDir(dir); }}
                                emptyState={
                                    <div className="collections-drawer__empty">
                                        No TP collections{drawerQuery ? ' match this filter' : ' yet'}.
                                    </div>
                                }
                            >
                                {filteredCollections.map((c, index) => {
                                    const isActive = c.id === activeId;
                                    return (
                                        <IndexTable.Row
                                            id={c.id}
                                            key={c.id}
                                            position={index}
                                            selected={isActive}
                                            onClick={() => openCollection(c.id)}
                                        >
                                            <IndexTable.Cell>
                                                <Text variant="bodyMd"
                                                      fontWeight={isActive ? 'semibold' : 'regular'}>
                                                    {c.title.replace(/^TP -\s*/, '')}
                                                </Text>
                                            </IndexTable.Cell>
                                            <IndexTable.Cell>
                                                <Text variant="bodySm" tone="subdued">
                                                    {c.created_by_display || '—'}
                                                </Text>
                                            </IndexTable.Cell>
                                            <IndexTable.Cell>
                                                <Text variant="bodySm" alignment="end">
                                                    {c.products_count ?? 0}
                                                </Text>
                                            </IndexTable.Cell>
                                            <IndexTable.Cell>
                                                <Text variant="bodySm" tone="subdued" alignment="end">
                                                    {effectiveUpdatedIso(c)
                                                        ? formatRelative(effectiveUpdatedIso(c))
                                                        : '—'}
                                                </Text>
                                            </IndexTable.Cell>
                                        </IndexTable.Row>
                                    );
                                })}
                            </IndexTable>
                        </div>
                    )}
                </aside>

                {/* Column 2: Builder */}
                <section className="collections-builder">
                    <div className="collections-builder__sticky-top">
                        <div className="collections-builder__header">
                            <span className="collections-builder__title">
                                {activeId ? 'Edit collection' : 'New collection'}
                            </span>
                            <span className="collections-builder__count">
                                {items.length} {items.length === 1 ? 'product' : 'products'}
                            </span>
                            {/* Clone (#5) lives in the panel header, away from
                                the Save button so it reads as a separate
                                action. Drafts a copy into the builder;
                                nothing persists until the user hits Save.
                                Delete sits next to Clone but is admin-only. */}
                            {activeId && !readOnly && (
                                <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6 }}>
                                    <Button onClick={cloneCollection}
                                            disabled={dirty}
                                            title={dirty
                                                ? 'Save or discard changes first, then clone'
                                                : 'Clone this collection — drafts a copy. Click Save to persist.'}>
                                        Clone
                                    </Button>
                                    {canEditPrice && (
                                        <Button tone="critical" variant="primary"
                                                onClick={deleteCollection}
                                                loading={deleting}
                                                disabled={deleting || saving}
                                                title="Delete this collection from Shopify. Cannot be undone.">
                                            Delete
                                        </Button>
                                    )}
                                </span>
                            )}
                        </div>
                        <div className="collections-builder__name-row">
                            <span className="collections-builder__prefix-chip">TP -</span>
                            <TextField label="Name" labelHidden value={name} onChange={setName}
                                       placeholder="Collection name (e.g. Nike Summer Wear)"
                                       disabled={readOnly || saving}
                                       maxLength={250} autoComplete="off" />
                            <div className="collections-builder__sort">
                                <Select label="Sort" labelHidden options={SORT_OPTIONS}
                                        value={sortOrder} onChange={setSortOrder}
                                        disabled={readOnly || saving} />
                            </div>
                        </div>
                        <div className="collections-builder__url-row">
                            <label className="collections-builder__url-label">Shopify URL</label>
                            <input className="collections-builder__url-input" readOnly
                                   value={savedUrl || ''}
                                   placeholder="(appears after save)"
                                   onFocus={e => e.target.select()} />
                            <Button onClick={copyUrl} disabled={!savedUrl}
                                    tone={copyState === 'fail' ? 'critical' : undefined}>
                                {copyState === 'ok' ? 'Copied!' :
                                 copyState === 'fail' ? 'Select + ⌘C' : 'Copy'}
                            </Button>
                        </div>
                        {!readOnly && (
                            <div className="collections-builder__save-row">
                                <span className="collections-builder__dirty">
                                    {dirty ? 'Unsaved changes' : (activeId ? 'All changes saved' : '')}
                                </span>
                                <Button variant="primary" disabled={!canSave || saving} loading={saving}
                                        onClick={save}>
                                    {activeId ? 'Save changes' : 'Create collection'}
                                </Button>
                            </div>
                        )}
                    </div>

                    {loadingActive && <div className="collections-builder__loading"><Spinner /></div>}

                    {saveError && (
                        <Banner tone="critical" onDismiss={() => setSaveError(null)}>
                            {saveError}
                        </Banner>
                    )}
                    {droppedNotice && (
                        <Banner tone="warning" onDismiss={() => setDroppedNotice(null)}>
                            {droppedNotice}
                        </Banner>
                    )}
                    {reorderPending && (
                        <Banner tone="info" onDismiss={() => setReorderPending(false)}>
                            Saved. Order is finalizing — refresh in a moment if it looks off.
                        </Banner>
                    )}

                    <ul className="collections-builder__items">
                        {items.map((p, idx) => (
                            <li key={p.id}
                                className={`collections-builder__row ${dropIdx === idx ? 'collections-builder__row--drop' : ''} ${dragIdx === idx ? 'collections-builder__row--dragging' : ''}`}
                                draggable={!readOnly && !saving}
                                onDragStart={onDragStart(idx)}
                                onDragOver={onDragOver(idx)}
                                onDrop={onDrop(idx)}
                                onDragEnd={onDragEnd}>
                                {!readOnly && (
                                    <span className="collections-builder__grip" aria-hidden="true">⠿</span>
                                )}
                                <span className="collections-builder__pos">{idx + 1}</span>
                                {p.image
                                    ? <img className="collections-builder__thumb" src={p.image} alt="" />
                                    : <div className="collections-builder__thumb collections-builder__thumb--empty" />}
                                <div className="collections-builder__row-meta">
                                    <div className="collections-builder__row-title">{p.title}</div>
                                    <div className="collections-builder__row-sub">
                                        {p.vendor || ''}
                                        {p.price
                                            ? <> · Shopify <strong>${p.price}</strong></>
                                            : null}
                                    </div>
                                </div>
                                {(() => {
                                    const tpStr = (tpPrices[p.id] ?? '').trim();
                                    const spStr = (p.price != null && p.price !== '')
                                        ? String(p.price) : '';
                                    // Effective display value: TP override if set,
                                    // else Shopify price. Same for admin + non-admin
                                    // — non-admin just can't type over it.
                                    const displayValue = tpStr || spStr;
                                    // Highlight when an explicit TP override exists
                                    // AND it differs (either direction) from Shopify.
                                    const tpNum = tpStr ? parseFloat(tpStr) : NaN;
                                    const spNum = spStr ? parseFloat(spStr) : NaN;
                                    const highlight = !isNaN(tpNum) && !isNaN(spNum)
                                                      && tpNum !== spNum;
                                    return (
                                        <label className="collections-builder__tp-price"
                                               title="Team Portal collection price (metafield: soccer.team_portal_collection_price)">
                                            <span className="collections-builder__tp-price-label">TP price</span>
                                            <span className={`collections-builder__tp-price-wrap${highlight ? ' collections-builder__tp-price-wrap--diff' : ''}`}>
                                                <span className="collections-builder__tp-price-sym">$</span>
                                                <input className="collections-builder__tp-price-input"
                                                    type="text" inputMode="decimal"
                                                    value={displayValue}
                                                    onChange={(e) => setTpPrice(p.id, e.target.value)}
                                                    disabled={!canEditPrice || saving}
                                                    placeholder="—" />
                                            </span>
                                        </label>
                                    );
                                })()}
                                {(() => {
                                    const inv = p.total_inventory ?? 0;
                                    const oos = inv === 0;
                                    return (
                                        <div className={`collections-builder__stock ${oos ? 'collections-builder__stock--oos' : ''}`}>
                                            <strong>{inv}</strong>
                                            <span> in stock</span>
                                        </div>
                                    );
                                })()}
                                {!readOnly && (
                                    <button className="collections-builder__remove"
                                            onClick={() => removeItem(p.id)}
                                            disabled={saving}
                                            aria-label="Remove">✕</button>
                                )}
                            </li>
                        ))}
                        {items.length === 0 && !loadingActive && (
                            <li className="collections-builder__empty">
                                No products yet. Search and click results to add them.
                            </li>
                        )}
                    </ul>

                </section>

                {/* Column 3: Search */}
                <aside className="collections-search">
                    <div className="collections-search__sticky-top">
                        <div className="collections-search__header">
                            <span className="collections-search__title">Add products</span>
                        </div>
                        <TextField label="Search" labelHidden value={q} onChange={setQ}
                                   placeholder="Search by name or brand…"
                                   prefix={<Icon source={SearchIcon} />}
                                   autoComplete="off" />
                        {/* Sort dropdown (#3) — only meaningful when results
                            are loaded. Sorts over what's been fetched so far. */}
                        {results.length > 0 && (
                            <div style={{ marginTop: 6 }}>
                                <Select label="Sort results" labelInline
                                        options={[
                                            { label: 'Relevance (default)', value: 'relevance' },
                                            { label: 'Most in stock',       value: 'inventory_desc' },
                                            { label: 'Least in stock',      value: 'inventory_asc' },
                                            { label: 'Price: low → high',   value: 'price_asc' },
                                            { label: 'Price: high → low',   value: 'price_desc' },
                                        ]}
                                        value={resultsSort}
                                        onChange={setResultsSort} />
                            </div>
                        )}
                    </div>
                    <ul className="collections-search__list">
                        {sortedResults.map(p => {
                            const added = inCollection.has(p.id);
                            const oos   = (p.total_inventory || 0) === 0;
                            return (
                                <li key={p.id}
                                    className={`collections-search__row ${added ? 'collections-search__row--added' : ''}`}
                                    onClick={() => !added && !readOnly && addToCollection(p)}>
                                    {p.image
                                        ? <img className="collections-search__thumb" src={p.image} alt="" />
                                        : <div className="collections-search__thumb collections-search__thumb--empty" />}
                                    <div className="collections-search__meta">
                                        <div className="collections-search__title-text">{p.title}</div>
                                        <div className="collections-search__sub">
                                            {p.vendor || ''}
                                            {p.price ? <> · <strong>${p.price}</strong></> : null}
                                            {p.status === 'UNLISTED' && <> · <Badge tone="attention">Unlisted</Badge></>}
                                        </div>
                                    </div>
                                    <div className={`collections-search__stock ${oos ? 'collections-search__stock--oos' : ''}`}>
                                        <strong>{p.total_inventory ?? 0}</strong>
                                        <span> in stock</span>
                                    </div>
                                    {added
                                        ? <span className="collections-search__added">✓ Added</span>
                                        : !readOnly && <span className="collections-search__add">+</span>}
                                </li>
                            );
                        })}
                        {!searching && debounced.trim().length >= 2 && results.length === 0 &&
                         lastFetchedQuery === debounced && (
                            <li className="collections-search__empty">No products match "{debounced}".</li>
                        )}
                        {debounced.trim().length < 2 && (
                            <li className="collections-search__empty">Type at least 2 characters to search.</li>
                        )}
                    </ul>
                    {searching && <div className="collections-search__loading"><Spinner size="small" /></div>}
                    {hasMore && !searching && (
                        <Button onClick={loadMore} fullWidth>Load more</Button>
                    )}
                </aside>
            </div>
        </div>
    );
}
